import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, SimpleGrid, Spinner, Stack, useToast } from '@chakra-ui/react';
import { Button, ButtonToggle } from '@peacecover/component';
import { Album, Camera, Card, Edit, SparkleStar, Xmark } from '@peacecover/icon';
import { Cropper } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import { t } from 'i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import useArtistQuery from '../../components/api/queries/useArtistQuery';
import useUserQuery from '../../components/api/queries/useUserQuery';
import { useGetUserBinder } from '../../domains/userBinders/ui/hooks/useGetUserBinder';
import { useSaveUserBinder } from '../../domains/userBinders/ui/hooks/useSaveUserBinder';
import DiaryBookCover from './components/DiaryBookCover';
import { ENABLE_DIARY_COVER_CUSTOMIZATION } from '../../constants/photoCardDiary';
import { getArtistId, getUserId } from '@pocapan/shared/utils/etcUtils';
import {
  DIARY_COVER_PRESETS,
  getDiaryCover,
  getDiaryCoverPreset,
  loadDiaryCoverMap
} from '@pocapan/shared/utils/photoCardDiaryCoverStorage';
import { queryGetNoCache } from '@pocapan/shared/utils/queryClient';
import { resizeImageFile } from '@pocapan/shared/utils/resizeImage';
import ToastMessageBox from '../../components/alerts/ToastMessageBox';
import TopNavigation from '../components/bar/TopNavigation';
import { STICKER_OPTION_MAP, STICKER_THEMES } from '@pocapan/assets/sticker';
import { toCanvas } from '@pocapan/shared/utils/SaveImage';
import '../../pages/MyPhotoCardCoverEditPage.css';

const COVER_PREVIEW_ASPECT_RATIO = 260 / 362;
const DEFAULT_EDITOR_POSITIONS = [
  { x: 28, y: 24 },
  { x: 68, y: 24 },
  { x: 32, y: 50 },
  { x: 68, y: 54 },
  { x: 48, y: 74 }
];
const BACKGROUND_COLOR_SWATCHS = DIARY_COVER_PRESETS.map((preset) => preset.coverColor);
const DEFAULT_TEXT_COLOR_SWATCHS = [
  '#FFFFFF',
  '#121317',
  '#FF6B6B',
  '#FFBE0B',
  '#2C78FF',
  '#8B5DFF'
];
const RESIZE_HANDLE_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const MIN_EDITOR_ITEM_SCALE = 0.52;
const MAX_EDITOR_ITEM_SCALE = 3.2;
const ROTATION_SNAP_THRESHOLD = 5;
const ROTATION_SNAP_ANGLES = [-180, -90, 0, 90, 180];

const STICKER_SHAPE_TABS = [
  { key: 'all', label: '전체' },
  { key: 'ornament', label: '장식' },
  { key: 'alphabet', label: '알파벳' },
  { key: 'number', label: '숫자' }
];

const COVER_EDIT_ACTIONS = [
  {
    key: 'text',
    labelKey: 'myPhotoCardDiary.addCoverText',
    defaultLabel: '텍스트',
    Icon: Edit,
    iconSize: 24
  },
  {
    key: 'sticker',
    labelKey: 'myPhotoCardDiary.addCoverSticker',
    defaultLabel: '스티커',
    Icon: SparkleStar,
    iconSize: 24
  },
  {
    key: 'photo-card',
    labelKey: 'myPhotoCardDiary.addCoverPhotocard',
    defaultLabel: '포토카드',
    Icon: Card,
    iconSize: 26
  },
  {
    key: 'background',
    labelKey: 'myPhotoCardDiary.changeCoverBackground',
    defaultLabel: '배경',
    Icon: Album,
    iconSize: 24
  }
];

let coverEditorItemSequence = 0;

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
};

const roundTo = (value, digits = 2) => {
  return Number(value.toFixed(digits));
};

const createEditorItemId = (prefix) => {
  coverEditorItemSequence += 1;
  return `${prefix}-${Date.now()}-${coverEditorItemSequence}`;
};

const waitForNextPaint = () => {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
};

const waitForDocumentFonts = async () => {
  if (!document.fonts?.ready) return;

  try {
    await document.fonts.ready;
  } catch (error) {
    console.log('커버 캡처 폰트 로딩 대기 오류:', error);
  }
};

const waitForElementImages = async (element) => {
  if (!element) return;

  const imageElements = Array.from(element.querySelectorAll('img'));
  await Promise.all(
    imageElements.map((imageElement) => {
      if (imageElement.complete && imageElement.naturalWidth > 0) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const handleComplete = () => {
          imageElement.removeEventListener('load', handleComplete);
          imageElement.removeEventListener('error', handleComplete);
          resolve();
        };

        imageElement.addEventListener('load', handleComplete);
        imageElement.addEventListener('error', handleComplete);
      });
    })
  );
};

const getNextEditorPosition = (itemCount) => {
  return DEFAULT_EDITOR_POSITIONS[itemCount % DEFAULT_EDITOR_POSITIONS.length];
};

const getPhotoCardImageUrl = (photoCard) => {
  return photoCard.mediumThumbnailUrl || photoCard.thumbnailUrl || photoCard.pictureUrl || '';
};

const getDistanceBetweenPoints = (x1, y1, x2, y2) => {
  return Math.hypot(x2 - x1, y2 - y1);
};

const getAngleBetweenPoints = (centerX, centerY, targetX, targetY) => {
  return (Math.atan2(targetY - centerY, targetX - centerX) * 180) / Math.PI;
};

const normalizeRotation = (rotation) => {
  const normalizedRotation = ((rotation % 360) + 360) % 360;
  return normalizedRotation > 180 ? normalizedRotation - 360 : normalizedRotation;
};

const getSnappedRotation = (rotation) => {
  const normalizedRotation = normalizeRotation(rotation);
  const nearestSnapAngle = ROTATION_SNAP_ANGLES.reduce((closestAngle, snapAngle) => {
    return Math.abs(snapAngle - normalizedRotation) < Math.abs(closestAngle - normalizedRotation)
      ? snapAngle
      : closestAngle;
  }, ROTATION_SNAP_ANGLES[0]);

  return Math.abs(nearestSnapAngle - normalizedRotation) <= ROTATION_SNAP_THRESHOLD
    ? nearestSnapAngle
    : roundTo(normalizedRotation, 1);
};

const buildTextItem = (text, textColor, itemCount) => {
  const position = getNextEditorPosition(itemCount);
  return {
    id: createEditorItemId('text'),
    kind: 'text',
    text,
    textColor,
    x: position.x,
    y: position.y,
    scale: 1,
    rotation: 0
  };
};

const buildStickerItem = (stickerKey, itemCount) => {
  const position = getNextEditorPosition(itemCount);
  return {
    id: createEditorItemId('sticker'),
    kind: 'sticker',
    stickerKey,
    x: position.x,
    y: position.y,
    scale: 1,
    rotation: 0
  };
};

const buildPhotoCardItem = (photoCard, itemCount) => {
  const position = getNextEditorPosition(itemCount);
  return {
    id: createEditorItemId('photo-card'),
    kind: 'photo-card',
    imageUrl: getPhotoCardImageUrl(photoCard),
    alt: photoCard.event?.name || photoCard.eventName || photoCard.photoCardId || 'photo card',
    x: position.x,
    y: position.y,
    scale: 1,
    rotation: 0
  };
};

function CoverEditSheet({ title, onClose, children, footer = null }) {
  return (
    <div className="diary-cover-edit-sheet-backdrop" role="presentation">
      <button
        type="button"
        className="diary-cover-edit-sheet-dismiss"
        aria-label={t('close', { defaultValue: '닫기' })}
        onClick={onClose}
      />
      <section
        className="diary-cover-edit-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="diary-cover-edit-sheet-header">
          <h2 className="diary-cover-edit-sheet-title">{title}</h2>
          <button
            type="button"
            className="diary-cover-edit-sheet-close"
            aria-label={t('close', { defaultValue: '닫기' })}
            onClick={onClose}
          >
            <Xmark size={20} />
          </button>
        </div>
        <div className="diary-cover-edit-sheet-body">{children}</div>
        {footer ? <div className="diary-cover-edit-sheet-footer">{footer}</div> : null}
      </section>
    </div>
  );
}

export default function MyPhotoCardCoverEditPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user_id: userId, member_id: memberId } = useParams();
  const artistId = getArtistId();
  const artist = useArtistQuery(artistId);
  const user = useUserQuery();
  const previewRef = useRef(null);
  const exportRef = useRef(null);
  const cameraInputRef = useRef(null);
  const albumInputRef = useRef(null);
  const cropperRef = useRef(null);
  const dragStateRef = useRef(null);
  const pendingCropObjectUrlRef = useRef('');
  const hasAutoHiddenNameRef = useRef(false);
  const boardPath = `/users/${userId}/binder`;
  const artistName = artist?.shortName || artist?.name;
  const currentUserId = user?.userId || getUserId() || '';
  const collectorName =
    currentUserId === userId
      ? user?.baseUserProfile?.nickname || user?.nickname || undefined
      : undefined;
  const likedMembers = useMemo(() => {
    const members = artist?.members || [];
    const filteredMembers = members.filter((member) => member.isLiked);
    return filteredMembers.length > 0 ? filteredMembers : members;
  }, [artist]);
  const fallbackMembers = useMemo(() => {
    return artist?.members || [];
  }, [artist]);
  const memberIndex = useMemo(() => {
    const likedMemberIndex = likedMembers.findIndex((member) => member.memberId === memberId);
    if (likedMemberIndex !== -1) return likedMemberIndex;
    return fallbackMembers.findIndex((member) => member.memberId === memberId);
  }, [fallbackMembers, likedMembers, memberId]);
  const member = useMemo(() => {
    return (
      likedMembers.find((likedMember) => likedMember.memberId === memberId) ||
      fallbackMembers.find((fallbackMember) => fallbackMember.memberId === memberId)
    );
  }, [fallbackMembers, likedMembers, memberId]);
  const [coverMap, setCoverMap] = useState({});
  const [draftCover, setDraftCover] = useState(null);
  const [isNameHidden, setIsNameHidden] = useState(false);
  const [editorItems, setEditorItems] = useState([]);
  const [activePanel, setActivePanel] = useState(null);
  const [textDraft, setTextDraft] = useState('');
  const [textColorDraft, setTextColorDraft] = useState(DIARY_COVER_PRESETS[0].textColor);
  const [backgroundColorDraft, setBackgroundColorDraft] = useState(BACKGROUND_COLOR_SWATCHS[0]);
  const [selectedStickerThemeKey, setSelectedStickerThemeKey] = useState(STICKER_THEMES[0].key);
  const [selectedStickerShapeKey, setSelectedStickerShapeKey] = useState(STICKER_SHAPE_TABS[0].key);
  const [customBackground, setCustomBackground] = useState(null);
  const [selectedCoverColorId, setSelectedCoverColorId] = useState('');
  const [pendingCropImage, setPendingCropImage] = useState('');
  const [isCropSaving, setIsCropSaving] = useState(false);
  const [draggingItemId, setDraggingItemId] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [isExportingCover, setIsExportingCover] = useState(false);
  const basePreset = getDiaryCoverPreset(draftCover?.colorId);
  const currentCoverColorId = selectedCoverColorId || draftCover?.colorId;
  const currentPreset = getDiaryCoverPreset(currentCoverColorId);
  const { data: userBinderMap = {} } = useGetUserBinder(currentUserId, userId, artistId, {
    enabled: !!currentUserId
  });
  const { mutateAsync: saveUserBinder } = useSaveUserBinder();
  const { data: memberPhotoCardData, isLoading: isMemberPhotoCardLoading } = useQuery(
    ['diaryPhotoCards', artistId, memberId],
    () => queryGetNoCache(`/artists/${artistId}/members/${memberId}/photo_cards`),
    {
      enabled: !!artistId && !!memberId,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always'
    }
  );

  const collectedPhotoCards = useMemo(() => {
    return (
      memberPhotoCardData?.events?.flatMap((eventGroup) => {
        return (eventGroup.photoCards || [])
          .filter((photoCard) => photoCard.collectedCount > 0)
          .map((photoCard) => {
            return {
              ...photoCard,
              eventName: eventGroup.event?.name
            };
          });
      }) || []
    );
  }, [memberPhotoCardData]);
  const previewCover = useMemo(() => {
    if (!draftCover) return null;
    return selectedCoverColorId ? { ...draftCover, colorId: selectedCoverColorId } : draftCover;
  }, [draftCover, selectedCoverColorId]);
  const textColorSwatches = useMemo(() => {
    return [
      currentPreset.textColor,
      ...DEFAULT_TEXT_COLOR_SWATCHS.filter((color) => {
        return color.toLowerCase() !== currentPreset.textColor.toLowerCase();
      })
    ];
  }, [currentPreset.textColor]);
  const selectedStickerTheme = useMemo(() => {
    return (
      STICKER_THEMES.find((theme) => theme.key === selectedStickerThemeKey) || STICKER_THEMES[0]
    );
  }, [selectedStickerThemeKey]);
  const visibleStickerOptions = useMemo(() => {
    if (selectedStickerShapeKey === 'all') {
      return Object.values(selectedStickerTheme?.stickers || {}).flat();
    }

    return selectedStickerTheme?.stickers?.[selectedStickerShapeKey] || [];
  }, [selectedStickerShapeKey, selectedStickerTheme]);
  const hasColorCustomization =
    !!selectedCoverColorId && selectedCoverColorId !== draftCover?.colorId;
  const hasCustomizations =
    isNameHidden || editorItems.length > 0 || !!customBackground || hasColorCustomization;
  const canInteract = !!draftCover && !!member;
  const userBinder = memberId ? userBinderMap[memberId] : null;
  const savedCoverImageUrl = userBinder?.imageUrl || '';
  const shouldShowSavedCoverImage = !!savedCoverImageUrl && !hasCustomizations;

  useEffect(() => {
    if (ENABLE_DIARY_COVER_CUSTOMIZATION) return;
    navigate(boardPath, { replace: true });
  }, [boardPath, navigate]);

  useEffect(() => {
    if (!userId || !artistId) return;
    setCoverMap(loadDiaryCoverMap({ userId, artistId }));
  }, [artistId, userId]);

  useEffect(() => {
    if (!artist || member) return;
    navigate(boardPath, { replace: true });
  }, [artist, boardPath, member, navigate]);

  useEffect(() => {
    if (!member) return;
    setDraftCover(
      getDiaryCover({
        coverMap,
        member,
        memberIndex: memberIndex >= 0 ? memberIndex : 0
      })
    );
  }, [coverMap, member, memberIndex]);

  const closeCropper = useCallback(() => {
    if (pendingCropObjectUrlRef.current) {
      URL.revokeObjectURL(pendingCropObjectUrlRef.current);
      pendingCropObjectUrlRef.current = '';
    }

    setPendingCropImage('');
    setIsCropSaving(false);
  }, []);

  useEffect(() => {
    return () => {
      if (pendingCropObjectUrlRef.current) {
        URL.revokeObjectURL(pendingCropObjectUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!memberId) return;
    setIsNameHidden(false);
    setEditorItems([]);
    setActivePanel(null);
    setTextDraft('');
    setTextColorDraft(basePreset.textColor);
    setSelectedStickerThemeKey(STICKER_THEMES[0].key);
    setSelectedStickerShapeKey(STICKER_SHAPE_TABS[0].key);
    setCustomBackground(null);
    setSelectedCoverColorId('');
    setBackgroundColorDraft(basePreset.coverColor);
    setSelectedItemId('');
    hasAutoHiddenNameRef.current = false;
    closeCropper();
  }, [basePreset.coverColor, basePreset.textColor, closeCropper, memberId]);

  useEffect(() => {
    const documentElement = document.documentElement;
    const { body } = document;
    const previousDocumentOverflow = documentElement.style.overflow;
    const previousDocumentHeight = documentElement.style.height;
    const previousDocumentOverscrollBehavior = documentElement.style.overscrollBehavior;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyHeight = body.style.height;
    const previousBodyOverscrollBehavior = body.style.overscrollBehavior;

    documentElement.style.overflow = 'hidden';
    documentElement.style.height = '100%';
    documentElement.style.overscrollBehavior = 'none';
    body.style.overflow = 'hidden';
    body.style.height = '100%';
    body.style.overscrollBehavior = 'none';

    return () => {
      documentElement.style.overflow = previousDocumentOverflow;
      documentElement.style.height = previousDocumentHeight;
      documentElement.style.overscrollBehavior = previousDocumentOverscrollBehavior;
      body.style.overflow = previousBodyOverflow;
      body.style.height = previousBodyHeight;
      body.style.overscrollBehavior = previousBodyOverscrollBehavior;
    };
  }, []);

  const goToBoard = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(boardPath, { replace: true });
  }, [boardPath, navigate]);

  const openBottomToast = useCallback(
    (message) => {
      toast({
        duration: 1600,
        render: () => <ToastMessageBox message={message} showOnBottom={true} />
      });
    },
    [toast]
  );

  const handleSave = useCallback(async () => {
    if (
      !artistId ||
      !memberId ||
      !member ||
      !draftCover ||
      !exportRef.current ||
      isExportingCover
    ) {
      return;
    }

    setIsExportingCover(true);

    try {
      await waitForNextPaint();
      await waitForDocumentFonts();
      await waitForElementImages(exportRef.current);

      const captureScale = Math.min(Math.max(window.devicePixelRatio || 2, 2), 4);
      const captureCanvas = await toCanvas(exportRef.current, {
        scale: captureScale,
        logging: false
      });
      const imageDataUrl = captureCanvas.toDataURL('image/png');

      await saveUserBinder({
        artistId,
        memberId,
        dataUrl: imageDataUrl,
        shouldUpdate: !!userBinder?.imageUrl
      });
      openBottomToast(
        t('myPhotoCardDiary.coverSaveCompleteMessage', {
          defaultValue: '커버를 저장했어요.'
        })
      );
    } catch (error) {
      console.log('커버 이미지 저장 오류:', error);
      openBottomToast(
        t('myPhotoCardDiary.coverSaveErrorMessage', {
          defaultValue: '커버를 저장하지 못했어요.'
        })
      );
    } finally {
      setIsExportingCover(false);
    }
  }, [
    artistId,
    draftCover,
    isExportingCover,
    member,
    memberId,
    openBottomToast,
    saveUserBinder,
    userBinder?.imageUrl
  ]);

  const handleReset = useCallback(() => {
    setIsNameHidden(false);
    setEditorItems([]);
    setTextDraft('');
    setTextColorDraft(basePreset.textColor);
    setBackgroundColorDraft(basePreset.coverColor);
    setCustomBackground(null);
    setSelectedCoverColorId('');
    setActivePanel(null);
    setSelectedItemId('');
    hasAutoHiddenNameRef.current = false;
    closeCropper();
  }, [basePreset.coverColor, basePreset.textColor, closeCropper]);

  const handleToolClick = useCallback(
    (actionKey) => {
      if (!canInteract) return;

      if (actionKey === 'text') {
        setTextDraft('');
        setTextColorDraft(currentPreset.textColor);
        setActivePanel('text');
        return;
      }

      if (actionKey === 'sticker') {
        setActivePanel('sticker');
        return;
      }

      if (actionKey === 'photo-card') {
        setActivePanel('photo-card');
        return;
      }

      if (actionKey === 'background') {
        setActivePanel('background');
      }
    },
    [canInteract, currentPreset.textColor]
  );

  const handleAddTextItem = useCallback(() => {
    const nextText = textDraft.trim();
    if (!nextText) return;

    let nextItemId = '';
    let shouldAutoHideName = false;
    setEditorItems((currentItems) => {
      const nextItem = buildTextItem(nextText, textColorDraft, currentItems.length);
      nextItemId = nextItem.id;
      shouldAutoHideName = currentItems.length === 0 && !hasAutoHiddenNameRef.current;
      return [...currentItems, nextItem];
    });
    if (shouldAutoHideName) {
      setIsNameHidden(true);
      hasAutoHiddenNameRef.current = true;
    }
    setSelectedItemId(nextItemId);
    setTextDraft('');
    setTextColorDraft(currentPreset.textColor);
    setActivePanel(null);
  }, [currentPreset.textColor, textColorDraft, textDraft]);

  const handleAddStickerItem = useCallback((stickerKey) => {
    let nextItemId = '';
    let shouldAutoHideName = false;
    setEditorItems((currentItems) => {
      const nextItem = buildStickerItem(stickerKey, currentItems.length);
      nextItemId = nextItem.id;
      shouldAutoHideName = currentItems.length === 0 && !hasAutoHiddenNameRef.current;
      return [...currentItems, nextItem];
    });
    if (shouldAutoHideName) {
      setIsNameHidden(true);
      hasAutoHiddenNameRef.current = true;
    }
    setSelectedItemId(nextItemId);
    setActivePanel(null);
  }, []);

  const handleAddPhotoCardItem = useCallback((photoCard) => {
    const imageUrl = getPhotoCardImageUrl(photoCard);
    if (!imageUrl) return;

    let nextItemId = '';
    let shouldAutoHideName = false;
    setEditorItems((currentItems) => {
      const nextItem = buildPhotoCardItem(photoCard, currentItems.length);
      nextItemId = nextItem.id;
      shouldAutoHideName = currentItems.length === 0 && !hasAutoHiddenNameRef.current;
      return [...currentItems, nextItem];
    });
    if (shouldAutoHideName) {
      setIsNameHidden(true);
      hasAutoHiddenNameRef.current = true;
    }
    setSelectedItemId(nextItemId);
    setActivePanel(null);
  }, []);

  const handleBackgroundAction = useCallback(
    (actionKey) => {
      if (actionKey === 'camera') {
        cameraInputRef.current?.click();
        setActivePanel(null);
        return;
      }

      if (actionKey === 'album') {
        albumInputRef.current?.click();
        setActivePanel(null);
        return;
      }

      if (actionKey === 'color') {
        setBackgroundColorDraft(
          customBackground?.type === 'color' ? customBackground.value : currentPreset.coverColor
        );
        setActivePanel('color');
      }
    },
    [currentPreset.coverColor, customBackground]
  );

  const handleBackgroundFileChange = useCallback((event) => {
    const [file] = event.target.files || [];
    event.target.value = '';

    if (!file) return;

    if (pendingCropObjectUrlRef.current) {
      URL.revokeObjectURL(pendingCropObjectUrlRef.current);
    }

    const objectUrl = URL.createObjectURL(file);
    pendingCropObjectUrlRef.current = objectUrl;
    setPendingCropImage(objectUrl);
    setActivePanel(null);
  }, []);

  const handleApplyBackgroundColor = useCallback(() => {
    const matchedPreset = DIARY_COVER_PRESETS.find((preset) => {
      return preset.coverColor.toLowerCase() === backgroundColorDraft.toLowerCase();
    });

    if (matchedPreset) {
      setSelectedCoverColorId(matchedPreset.id);
      setCustomBackground(null);
      setActivePanel(null);
      return;
    }

    setCustomBackground({
      type: 'color',
      value: backgroundColorDraft
    });
    setSelectedCoverColorId('');
    setActivePanel(null);
  }, [backgroundColorDraft]);

  const handleApplyCropBackground = useCallback(async () => {
    if (!cropperRef.current) return;
    setIsCropSaving(true);

    try {
      const sourceCanvas = cropperRef.current.getCanvas();
      if (!sourceCanvas) {
        throw new Error('missing crop canvas');
      }

      const blob = await new Promise((resolve, reject) => {
        sourceCanvas.toBlob(
          (nextBlob) => {
            if (!nextBlob) {
              reject(new Error('failed to create crop blob'));
              return;
            }

            resolve(nextBlob);
          },
          'image/jpeg',
          0.96
        );
      });

      const croppedFile = new File([blob], 'cover-background.jpg', { type: 'image/jpeg' });
      const resizedBase64 = await resizeImageFile(croppedFile, {
        maxWidth: 1300,
        maxHeight: 1810,
        format: 'JPEG',
        quality: 95,
        outputType: 'base64',
        rotation: 0
      });

      setCustomBackground({
        type: 'image',
        value: resizedBase64
      });
      closeCropper();
    } catch (error) {
      console.log('커버 배경 크롭 오류:', error);
      openBottomToast(
        t('myPhotoCardDiary.coverBackgroundCropError', {
          defaultValue: '배경 이미지를 불러오지 못했어요.'
        })
      );
    } finally {
      setIsCropSaving(false);
    }
  }, [closeCropper, openBottomToast]);

  const updateEditorItemPosition = useCallback((itemId, nextX, nextY) => {
    setEditorItems((currentItems) => {
      return currentItems.map((item) => {
        if (item.id !== itemId) return item;
        return {
          ...item,
          x: roundTo(nextX),
          y: roundTo(nextY)
        };
      });
    });
  }, []);

  const updateEditorItemTransform = useCallback((itemId, nextTransform) => {
    setEditorItems((currentItems) => {
      return currentItems.map((item) => {
        if (item.id !== itemId) return item;
        return {
          ...item,
          ...nextTransform
        };
      });
    });
  }, []);

  const bringEditorItemToFront = useCallback((itemId) => {
    setEditorItems((currentItems) => {
      const itemIndex = currentItems.findIndex((item) => item.id === itemId);
      if (itemIndex < 0 || itemIndex === currentItems.length - 1) return currentItems;

      const nextItems = [...currentItems];
      const [selectedItem] = nextItems.splice(itemIndex, 1);
      nextItems.push(selectedItem);
      return nextItems;
    });
  }, []);

  const selectEditorItem = useCallback(
    (itemId) => {
      setSelectedItemId(itemId);
      bringEditorItemToFront(itemId);
    },
    [bringEditorItemToFront]
  );

  const handleDeleteEditorItem = useCallback((itemId) => {
    dragStateRef.current = null;
    setDraggingItemId('');
    setSelectedItemId((currentSelectedItemId) => {
      return currentSelectedItemId === itemId ? '' : currentSelectedItemId;
    });
    setEditorItems((currentItems) => {
      return currentItems.filter((item) => item.id !== itemId);
    });
  }, []);

  const handleItemPointerDown = useCallback(
    (item) => (event) => {
      if (!previewRef.current) return;

      const containerRect = previewRef.current.getBoundingClientRect();
      const itemElement = event.currentTarget.closest('.diary-cover-edit-overlay-item');
      if (!itemElement) return;
      const itemRect = itemElement.getBoundingClientRect();

      selectEditorItem(item.id);
      dragStateRef.current = {
        mode: 'drag',
        itemId: item.id,
        startClientX: event.clientX,
        startClientY: event.clientY,
        originCenterX: itemRect.left - containerRect.left + itemRect.width / 2,
        originCenterY: itemRect.top - containerRect.top + itemRect.height / 2,
        itemWidth: itemRect.width,
        itemHeight: itemRect.height,
        containerWidth: containerRect.width,
        containerHeight: containerRect.height
      };

      setDraggingItemId(item.id);
      event.preventDefault();
    },
    [selectEditorItem]
  );

  const handleResizePointerDown = useCallback(
    (item) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!previewRef.current) return;

      const containerRect = previewRef.current.getBoundingClientRect();
      const itemElement = event.currentTarget.closest('.diary-cover-edit-overlay-item');
      if (!itemElement) return;
      const itemRect = itemElement.getBoundingClientRect();
      const centerX = itemRect.left - containerRect.left + itemRect.width / 2;
      const centerY = itemRect.top - containerRect.top + itemRect.height / 2;
      const startDistance = getDistanceBetweenPoints(
        centerX,
        centerY,
        event.clientX - containerRect.left,
        event.clientY - containerRect.top
      );

      selectEditorItem(item.id);
      dragStateRef.current = {
        mode: 'resize',
        itemId: item.id,
        centerX,
        centerY,
        startDistance: Math.max(startDistance, 1),
        startScale: item.scale || 1
      };
    },
    [selectEditorItem]
  );

  const handleRotatePointerDown = useCallback(
    (item) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!previewRef.current) return;

      const containerRect = previewRef.current.getBoundingClientRect();
      const itemElement = event.currentTarget.closest('.diary-cover-edit-overlay-item');
      if (!itemElement) return;
      const itemRect = itemElement.getBoundingClientRect();
      const centerX = itemRect.left - containerRect.left + itemRect.width / 2;
      const centerY = itemRect.top - containerRect.top + itemRect.height / 2;

      selectEditorItem(item.id);
      dragStateRef.current = {
        mode: 'rotate',
        itemId: item.id,
        centerX,
        centerY,
        startAngle: getAngleBetweenPoints(
          centerX,
          centerY,
          event.clientX - containerRect.left,
          event.clientY - containerRect.top
        ),
        startRotation: item.rotation || 0
      };
    },
    [selectEditorItem]
  );

  const handlePreviewPointerDownCapture = useCallback((event) => {
    if (event.target.closest('.diary-cover-edit-overlay-item')) return;
    setSelectedItemId('');
  }, []);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const interactionState = dragStateRef.current;
      if (!interactionState) return;

      event.preventDefault();

      if (interactionState.mode === 'drag') {
        const nextCenterX = clamp(
          interactionState.originCenterX + (event.clientX - interactionState.startClientX),
          0,
          interactionState.containerWidth
        );
        const nextCenterY = clamp(
          interactionState.originCenterY + (event.clientY - interactionState.startClientY),
          0,
          interactionState.containerHeight
        );

        updateEditorItemPosition(
          interactionState.itemId,
          (nextCenterX / interactionState.containerWidth) * 100,
          (nextCenterY / interactionState.containerHeight) * 100
        );
        return;
      }

      if (!previewRef.current) return;
      const containerRect = previewRef.current.getBoundingClientRect();
      const pointerX = event.clientX - containerRect.left;
      const pointerY = event.clientY - containerRect.top;

      if (interactionState.mode === 'resize') {
        const nextDistance = getDistanceBetweenPoints(
          interactionState.centerX,
          interactionState.centerY,
          pointerX,
          pointerY
        );
        const nextScale = clamp(
          interactionState.startScale * (nextDistance / interactionState.startDistance),
          MIN_EDITOR_ITEM_SCALE,
          MAX_EDITOR_ITEM_SCALE
        );

        updateEditorItemTransform(interactionState.itemId, {
          scale: roundTo(nextScale, 3)
        });
        return;
      }

      if (interactionState.mode === 'rotate') {
        const nextRotation = getSnappedRotation(
          interactionState.startRotation +
            (getAngleBetweenPoints(
              interactionState.centerX,
              interactionState.centerY,
              pointerX,
              pointerY
            ) -
              interactionState.startAngle)
        );

        updateEditorItemTransform(interactionState.itemId, {
          rotation: nextRotation
        });
      }
    };

    const handlePointerUp = () => {
      if (dragStateRef.current?.mode === 'drag') {
        setDraggingItemId('');
      }
      dragStateRef.current = null;
      setDraggingItemId('');
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [updateEditorItemPosition, updateEditorItemTransform]);

  const renderEditorItemContent = (item) => {
    if (item.kind === 'text') {
      return (
        <span
          className="diary-cover-edit-text-sticker"
          style={{ color: item.textColor || currentPreset.textColor }}
        >
          {item.text}
        </span>
      );
    }

    if (item.kind === 'sticker') {
      const stickerOption = STICKER_OPTION_MAP[item.stickerKey];
      if (!stickerOption) return null;

      if (stickerOption.imageSrc) {
        return (
          <span className="diary-cover-edit-icon-sticker diary-cover-edit-icon-sticker--image">
            <img src={stickerOption.imageSrc} alt={stickerOption.label} draggable={false} />
          </span>
        );
      }

      const StickerIcon = stickerOption.Icon;
      if (!StickerIcon) return null;

      return (
        <span
          className="diary-cover-edit-icon-sticker"
          style={{
            '--cover-sticker-background': stickerOption.background,
            '--cover-sticker-color': stickerOption.color
          }}
        >
          <StickerIcon size={28} />
        </span>
      );
    }

    if (item.kind === 'photo-card') {
      return (
        <span className="diary-cover-edit-photo-card-sticker">
          <img src={item.imageUrl} alt={item.alt} draggable={false} />
        </span>
      );
    }

    return null;
  };

  const renderEditorItem = (item, { interactive = true } = {}) => {
    const isSelected = interactive && (selectedItemId === item.id || draggingItemId === item.id);
    const content = renderEditorItemContent(item);
    if (!content) return null;

    const sharedProps = {
      key: item.id,
      className: `diary-cover-edit-overlay-item diary-cover-edit-overlay-item--${item.kind} ${
        isSelected ? 'is-selected ' : ''
      }${draggingItemId === item.id ? 'is-dragging' : ''}`.trim(),
      style: {
        '--cover-editor-item-scale': item.scale || 1,
        left: `${item.x}%`,
        top: `${item.y}%`,
        transform: `translate(-50%, -50%) rotate(${item.rotation || 0}deg) scale(${item.scale || 1})`
      }
    };
    const bodyProps = interactive
      ? {
          type: 'button',
          className: 'diary-cover-edit-overlay-item-body',
          onPointerDown: handleItemPointerDown(item),
          onFocus: () => selectEditorItem(item.id),
          onClick: () => selectEditorItem(item.id)
        }
      : null;
    const selectionControls =
      interactive && isSelected
        ? <>
            <span className="diary-cover-edit-selection-frame" aria-hidden="true" />
            <button
              type="button"
              className="diary-cover-edit-delete-handle"
              aria-label={t('myPhotoCardDiary.coverDeleteHandle', { defaultValue: '삭제' })}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleDeleteEditorItem(item.id);
              }}
            >
              <Xmark size={12} />
            </button>
            <span className="diary-cover-edit-rotate-stem" aria-hidden="true" />
            <button
              type="button"
              className="diary-cover-edit-rotate-handle"
              aria-label={t('myPhotoCardDiary.coverRotateHandle', { defaultValue: '회전' })}
              onPointerDown={handleRotatePointerDown(item)}
            />
            {RESIZE_HANDLE_POSITIONS.map((handlePosition) => {
              return (
                <button
                  key={handlePosition}
                  type="button"
                  className={`diary-cover-edit-resize-handle diary-cover-edit-resize-handle--${handlePosition}`}
                  aria-label={t('myPhotoCardDiary.coverResizeHandle', {
                    defaultValue: '크기 조절'
                  })}
                  onPointerDown={handleResizePointerDown(item)}
                />
              );
            })}
          </>
        : null;

    return (
      <div {...sharedProps}>
        {interactive
          ? <button {...bodyProps}>{content}</button>
          : <div className="diary-cover-edit-overlay-item-static">{content}</div>}
        {selectionControls}
      </div>
    );
  };

  if (!ENABLE_DIARY_COVER_CUSTOMIZATION) return null;

  return (
    <main
      className="diary-cover-edit-root"
      style={{
        '--diary-cover-color': currentPreset.coverColor,
        '--diary-cover-text': currentPreset.textColor,
        '--diary-page-color': currentPreset.pageColor,
        '--diary-page-edge-color': currentPreset.pageEdgeColor,
        '--diary-accent-color': currentPreset.accentColor
      }}
    >
      <TopNavigation
        type="back"
        onBackClick={goToBoard}
        className="diary-cover-edit-navigation"
        centerComponent={
          <h1 className="diary-cover-edit-title">
            {t('myPhotoCardDiary.editCoverTitle', { defaultValue: '커버 꾸미기' })}
          </h1>
        }
      />

      <Stack className="diary-cover-edit-content" spacing={'22px'}>
        <section className="diary-cover-edit-preview-section" aria-label="cover preview">
          <Box
            className="diary-cover-edit-preview"
            ref={previewRef}
            onPointerDownCapture={handlePreviewPointerDownCapture}
          >
            {previewCover && member
              ? <DiaryBookCover
                  cover={previewCover}
                  coverImageUrl={shouldShowSavedCoverImage ? savedCoverImageUrl : undefined}
                  title={previewCover.title}
                  titleHidden={isNameHidden}
                  subtitleHidden={isNameHidden}
                  badgeHidden={isNameHidden}
                  memberName={member.name}
                  subtitle={artistName || undefined}
                  badgeText={collectorName}
                  backgroundImage={
                    customBackground?.type === 'image' ? customBackground.value : undefined
                  }
                  backgroundColor={
                    customBackground?.type === 'color' ? customBackground.value : undefined
                  }
                >
                  {editorItems.length > 0
                    ? <div className="diary-cover-edit-overlay">
                        {editorItems.map(renderEditorItem)}
                      </div>
                    : null}
                </DiaryBookCover>
              : null}
          </Box>

          <div className="diary-cover-edit-name-toggle">
            <ButtonToggle
              type="check"
              size="lg"
              checked={isNameHidden}
              onChange={setIsNameHidden}
              aria-label={t('myPhotoCardDiary.hideCoverName', { defaultValue: '이름 숨기기' })}
            />
            <button
              type="button"
              className="diary-cover-edit-name-toggle-text"
              onClick={() => setIsNameHidden((currentValue) => !currentValue)}
            >
              {t('myPhotoCardDiary.hideCoverName', { defaultValue: '이름 숨기기' })}
            </button>
          </div>
        </section>

        <SimpleGrid
          className="diary-cover-edit-tools"
          columns={COVER_EDIT_ACTIONS.length}
          spacing={'20px'}
        >
          {COVER_EDIT_ACTIONS.map((action) => {
            const ActionIcon = action.Icon;

            return (
              <button
                key={action.key}
                type="button"
                className="diary-cover-edit-tool-button"
                onClick={() => handleToolClick(action.key)}
                disabled={!canInteract}
              >
                <span className="diary-cover-edit-tool-icon" aria-hidden="true">
                  <ActionIcon size={action.iconSize} className="diary-cover-edit-tool-svg" />
                </span>
                <span className="diary-cover-edit-tool-label">
                  {t(action.labelKey, { defaultValue: action.defaultLabel })}
                </span>
              </button>
            );
          })}
        </SimpleGrid>
      </Stack>

      <Stack className="diary-cover-edit-cta" direction={'row'} spacing={'10px'}>
        <Button
          type="button"
          btnColor="black"
          size="large"
          className="diary-cover-edit-reset-button"
          onClick={handleReset}
          disabled={!hasCustomizations || isExportingCover}
        >
          {t('myPhotoCardDiary.resetCoverButton', { defaultValue: '초기화' })}
        </Button>
        <Button
          type="button"
          btnColor="primary"
          size="large"
          className="diary-cover-edit-save-button"
          onClick={handleSave}
          disabled={!member || !draftCover || !hasCustomizations || isExportingCover}
        >
          <span className="diary-cover-edit-save-button-content">
            {isExportingCover
              ? <>
                  <Spinner
                    size="sm"
                    thickness="2px"
                    speed="0.65s"
                    color="currentColor"
                    emptyColor="rgba(255, 255, 255, 0.22)"
                  />
                  <span>
                    {t('myPhotoCardDiary.savingCoverButton', { defaultValue: '저장하는 중...' })}
                  </span>
                </>
              : <span>
                  {t('myPhotoCardDiary.saveCoverButton', { defaultValue: '이대로 저장하기' })}
                </span>}
          </span>
        </Button>
      </Stack>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="diary-cover-edit-hidden-input"
        onChange={handleBackgroundFileChange}
      />
      <input
        ref={albumInputRef}
        type="file"
        accept="image/*"
        className="diary-cover-edit-hidden-input"
        onChange={handleBackgroundFileChange}
      />

      <div className="diary-cover-edit-export-stage" aria-hidden="true">
        <div className="diary-cover-edit-export-preview" ref={exportRef}>
          {previewCover && member
            ? <DiaryBookCover
                cover={previewCover}
                coverImageUrl={shouldShowSavedCoverImage ? savedCoverImageUrl : undefined}
                title={previewCover.title}
                titleHidden={isNameHidden}
                subtitleHidden={isNameHidden}
                badgeHidden={isNameHidden}
                memberName={member.name}
                subtitle={artistName || undefined}
                badgeText={collectorName}
                backgroundImage={
                  customBackground?.type === 'image' ? customBackground.value : undefined
                }
                backgroundColor={
                  customBackground?.type === 'color' ? customBackground.value : undefined
                }
              >
                {editorItems.length > 0
                  ? <div className="diary-cover-edit-overlay">
                      {editorItems.map((item) => renderEditorItem(item, { interactive: false }))}
                    </div>
                  : null}
              </DiaryBookCover>
            : null}
        </div>
      </div>

      {activePanel === 'text'
        ? <CoverEditSheet
            title={t('myPhotoCardDiary.addCoverText', { defaultValue: '텍스트 추가' })}
            onClose={() => setActivePanel(null)}
            footer={
              <Stack direction={'row'} spacing={'10px'}>
                <Button
                  type="button"
                  btnColor="black"
                  size="large"
                  className="diary-cover-edit-sheet-secondary-button"
                  onClick={() => setActivePanel(null)}
                >
                  {t('cancel', { defaultValue: '취소' })}
                </Button>
                <Button
                  type="button"
                  btnColor="primary"
                  size="large"
                  className="diary-cover-edit-sheet-primary-button"
                  onClick={handleAddTextItem}
                  disabled={!textDraft.trim()}
                >
                  {t('myPhotoCardDiary.addCoverText', { defaultValue: '텍스트 추가' })}
                </Button>
              </Stack>
            }
          >
            <label className="diary-cover-edit-sheet-field" htmlFor="cover-edit-text-input">
              {t('myPhotoCardDiary.coverTextInputLabel', {
                defaultValue: '붙일 문구를 입력해 주세요.'
              })}
            </label>
            <textarea
              id="cover-edit-text-input"
              className="diary-cover-edit-sheet-textarea"
              rows={4}
              maxLength={40}
              value={textDraft}
              onChange={(event) => setTextDraft(event.target.value)}
              placeholder={t('myPhotoCardDiary.coverTextInputPlaceholder', {
                defaultValue: '예: 효지의 오늘도 반짝이는 하루'
              })}
            />
            <div className="diary-cover-edit-text-color-section">
              <div className="diary-cover-edit-sheet-field">
                {t('myPhotoCardDiary.coverTextColorLabel', {
                  defaultValue: '텍스트 색상'
                })}
              </div>
              <div className="diary-cover-edit-text-color-swatches">
                {textColorSwatches.map((color) => {
                  const isSelected = textColorDraft.toLowerCase() === color.toLowerCase();

                  return (
                    <button
                      key={color}
                      type="button"
                      className={`diary-cover-edit-text-color-swatch ${isSelected ? 'is-selected' : ''}`.trim()}
                      style={{ '--cover-text-color-swatch': color }}
                      aria-label={color}
                      onClick={() => setTextColorDraft(color)}
                    />
                  );
                })}
              </div>
            </div>
          </CoverEditSheet>
        : null}

      {activePanel === 'sticker'
        ? <CoverEditSheet
            title={t('myPhotoCardDiary.addCoverSticker', { defaultValue: '스티커 추가' })}
            onClose={() => setActivePanel(null)}
          >
            <div className="diary-cover-edit-sticker-layout">
              <div
                className="diary-cover-edit-sticker-theme-tabs"
                role="tablist"
                aria-label={t('myPhotoCardDiary.coverStickerThemeTabs', {
                  defaultValue: '스티커 테마'
                })}
              >
                {STICKER_THEMES.map((theme) => {
                  const isSelected = theme.key === selectedStickerThemeKey;

                  return (
                    <button
                      key={theme.key}
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      className={`diary-cover-edit-sticker-theme-tab ${isSelected ? 'is-selected' : ''}`.trim()}
                      onClick={() => setSelectedStickerThemeKey(theme.key)}
                    >
                      {theme.label}
                    </button>
                  );
                })}
              </div>

              <div
                className="diary-cover-edit-sticker-shape-tabs"
                role="tablist"
                aria-label={t('myPhotoCardDiary.coverStickerShapeTabs', {
                  defaultValue: '스티커 쉐잎 종류'
                })}
              >
                {STICKER_SHAPE_TABS.map((shapeTab) => {
                  const isSelected = shapeTab.key === selectedStickerShapeKey;

                  return (
                    <button
                      key={shapeTab.key}
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      className={`diary-cover-edit-sticker-shape-tab ${isSelected ? 'is-selected' : ''}`.trim()}
                      onClick={() => setSelectedStickerShapeKey(shapeTab.key)}
                    >
                      {shapeTab.label}
                    </button>
                  );
                })}
              </div>

              {visibleStickerOptions.length > 0
                ? <SimpleGrid
                    className="diary-cover-edit-sticker-grid"
                    columns={4}
                    spacing={'12px'}
                  >
                    {visibleStickerOptions.map((stickerOption) => {
                      if (stickerOption.imageSrc) {
                        return (
                          <button
                            key={stickerOption.key}
                            type="button"
                            className="diary-cover-edit-sticker-option"
                            onClick={() => handleAddStickerItem(stickerOption.key)}
                            aria-label={stickerOption.label}
                          >
                            <span className="diary-cover-edit-sticker-option-icon diary-cover-edit-sticker-option-icon--image">
                              <img
                                src={stickerOption.imageSrc}
                                alt=""
                                draggable={false}
                                loading="lazy"
                              />
                            </span>
                          </button>
                        );
                      }

                      const StickerIcon = stickerOption.Icon;
                      if (!StickerIcon) return null;

                      return (
                        <button
                          key={stickerOption.key}
                          type="button"
                          className="diary-cover-edit-sticker-option"
                          onClick={() => handleAddStickerItem(stickerOption.key)}
                          aria-label={stickerOption.label}
                        >
                          <span
                            className="diary-cover-edit-sticker-option-icon"
                            style={{
                              '--cover-sticker-background': stickerOption.background,
                              '--cover-sticker-color': stickerOption.color
                            }}
                          >
                            <StickerIcon size={26} />
                          </span>
                        </button>
                      );
                    })}
                  </SimpleGrid>
                : <div className="diary-cover-edit-sticker-empty">
                    <strong>
                      {t('myPhotoCardDiary.coverStickerEmptyTitle', {
                        defaultValue: '스티커 레이아웃 준비 완료'
                      })}
                    </strong>
                    <span>
                      {t('myPhotoCardDiary.coverStickerEmptyDescription', {
                        defaultValue:
                          '테마와 쉐잎 탭 구조를 먼저 잡아두었어요. 스티커는 다음 단계에서 채워 넣을 수 있어요.'
                      })}
                    </span>
                  </div>}
            </div>
          </CoverEditSheet>
        : null}

      {activePanel === 'photo-card'
        ? <CoverEditSheet
            title={t('myPhotoCardDiary.addCoverPhotocard', { defaultValue: '포토카드 추가' })}
            onClose={() => setActivePanel(null)}
          >
            {isMemberPhotoCardLoading
              ? <div className="diary-cover-edit-sheet-empty">
                  {t('loading', { defaultValue: '불러오는 중...' })}
                </div>
              : null}
            {!isMemberPhotoCardLoading && collectedPhotoCards.length === 0
              ? <div className="diary-cover-edit-sheet-empty">
                  {t('myPhotoCardDiary.coverPhotocardEmpty', {
                    defaultValue: '추가할 수 있는 포토카드가 아직 없어요.'
                  })}
                </div>
              : null}
            {!isMemberPhotoCardLoading && collectedPhotoCards.length > 0
              ? <div className="diary-cover-edit-photo-card-grid">
                  {collectedPhotoCards.map((photoCard) => {
                    const imageUrl = getPhotoCardImageUrl(photoCard);
                    if (!imageUrl) return null;

                    return (
                      <button
                        key={photoCard.photoCardId}
                        type="button"
                        className="diary-cover-edit-photo-card-option"
                        onClick={() => handleAddPhotoCardItem(photoCard)}
                      >
                        <img src={imageUrl} alt={photoCard.eventName || photoCard.photoCardId} />
                      </button>
                    );
                  })}
                </div>
              : null}
          </CoverEditSheet>
        : null}

      {activePanel === 'background'
        ? <CoverEditSheet
            title={t('myPhotoCardDiary.changeCoverBackground', { defaultValue: '배경 바꾸기' })}
            onClose={() => setActivePanel(null)}
          >
            <div className="diary-cover-edit-background-options">
              <button
                type="button"
                className="diary-cover-edit-background-option"
                onClick={() => handleBackgroundAction('camera')}
              >
                <span className="diary-cover-edit-background-option-icon" aria-hidden="true">
                  <Camera size={22} />
                </span>
                <span className="diary-cover-edit-background-option-copy">
                  <strong>
                    {t('myPhotoCardDiary.coverBackgroundCamera', { defaultValue: '카메라로 찍기' })}
                  </strong>
                  <span>
                    {t('myPhotoCardDiary.coverBackgroundCameraDescription', {
                      defaultValue: '촬영한 이미지를 커버 비율에 맞게 잘라서 배경으로 쓸 수 있어요.'
                    })}
                  </span>
                </span>
              </button>

              <button
                type="button"
                className="diary-cover-edit-background-option"
                onClick={() => handleBackgroundAction('album')}
              >
                <span className="diary-cover-edit-background-option-icon" aria-hidden="true">
                  <Album size={22} />
                </span>
                <span className="diary-cover-edit-background-option-copy">
                  <strong>
                    {t('myPhotoCardDiary.coverBackgroundAlbum', { defaultValue: '앨범에서 선택' })}
                  </strong>
                  <span>
                    {t('myPhotoCardDiary.coverBackgroundAlbumDescription', {
                      defaultValue: '기기 앨범에 있는 이미지를 골라 배경으로 사용할 수 있어요.'
                    })}
                  </span>
                </span>
              </button>

              <button
                type="button"
                className="diary-cover-edit-background-option"
                onClick={() => handleBackgroundAction('color')}
              >
                <span
                  className="diary-cover-edit-background-option-icon diary-cover-edit-background-option-icon--color"
                  aria-hidden="true"
                >
                  <span
                    className="diary-cover-edit-background-color-preview"
                    style={{ backgroundColor: backgroundColorDraft }}
                  />
                </span>
                <span className="diary-cover-edit-background-option-copy">
                  <strong>
                    {t('myPhotoCardDiary.coverBackgroundColor', { defaultValue: '색상 변경' })}
                  </strong>
                  <span>
                    {t('myPhotoCardDiary.coverBackgroundColorDescription', {
                      defaultValue: '원하는 단색으로 커버 배경 톤을 바꿀 수 있어요.'
                    })}
                  </span>
                </span>
              </button>
            </div>
          </CoverEditSheet>
        : null}

      {activePanel === 'color'
        ? <CoverEditSheet
            title={t('myPhotoCardDiary.coverBackgroundColor', { defaultValue: '색상 변경' })}
            onClose={() => setActivePanel(null)}
            footer={
              <Stack direction={'row'} spacing={'10px'}>
                <Button
                  type="button"
                  btnColor="black"
                  size="large"
                  className="diary-cover-edit-sheet-secondary-button"
                  onClick={() => setActivePanel(null)}
                >
                  {t('cancel', { defaultValue: '취소' })}
                </Button>
                <Button
                  type="button"
                  btnColor="primary"
                  size="large"
                  className="diary-cover-edit-sheet-primary-button"
                  onClick={handleApplyBackgroundColor}
                >
                  {t('apply', { defaultValue: '적용' })}
                </Button>
              </Stack>
            }
          >
            <div className="diary-cover-edit-color-swatches">
              {BACKGROUND_COLOR_SWATCHS.map((color) => {
                const isSelected = backgroundColorDraft.toLowerCase() === color.toLowerCase();

                return (
                  <button
                    key={color}
                    type="button"
                    className={`diary-cover-edit-color-swatch ${isSelected ? 'is-selected' : ''}`.trim()}
                    style={{ backgroundColor: color }}
                    aria-label={color}
                    onClick={() => setBackgroundColorDraft(color)}
                  />
                );
              })}
            </div>
          </CoverEditSheet>
        : null}

      {pendingCropImage
        ? <div className="diary-cover-edit-cropper-backdrop" role="dialog" aria-modal="true">
            <div className="diary-cover-edit-cropper-header">
              <button
                type="button"
                className="diary-cover-edit-cropper-close"
                aria-label={t('cancel', { defaultValue: '취소' })}
                onClick={closeCropper}
              >
                <Xmark size={24} />
              </button>
              <h2 className="diary-cover-edit-cropper-title">
                {t('myPhotoCardDiary.coverBackgroundCropTitle', {
                  defaultValue: '배경 위치 조정'
                })}
              </h2>
              <div className="diary-cover-edit-cropper-spacer" aria-hidden="true" />
            </div>

            <div className="diary-cover-edit-cropper-stage">
              <Cropper
                ref={cropperRef}
                src={pendingCropImage}
                stencilProps={{ aspectRatio: COVER_PREVIEW_ASPECT_RATIO }}
                className="diary-cover-edit-cropper"
              />
            </div>

            <div className="diary-cover-edit-cropper-footer">
              <p className="diary-cover-edit-cropper-description">
                {t('myPhotoCardDiary.coverBackgroundCropDescription', {
                  defaultValue: '커버에 보여질 영역을 맞춘 뒤 적용해 주세요.'
                })}
              </p>
              <Stack direction={'row'} spacing={'10px'}>
                <Button
                  type="button"
                  btnColor="black"
                  size="large"
                  className="diary-cover-edit-sheet-secondary-button"
                  onClick={closeCropper}
                >
                  {t('cancel', { defaultValue: '취소' })}
                </Button>
                <Button
                  type="button"
                  btnColor="primary"
                  size="large"
                  className="diary-cover-edit-sheet-primary-button"
                  onClick={handleApplyCropBackground}
                  disabled={isCropSaving}
                >
                  {t('apply', { defaultValue: '적용' })}
                </Button>
              </Stack>
            </div>
          </div>
        : null}
    </main>
  );
}
